import React from 'react';
import { Button, Eyebrow } from '../primitives';
import { ArrowLongRight } from '../icons';

/** PHLD.ai — Hero. The proposition in human terms. One soft CTA. No badges, no hype. */
export function Hero() {
  const section: React.CSSProperties = {
    padding: 'var(--space-9) var(--gutter) var(--space-10)',
    maxWidth: 'var(--container)',
    margin: '0 auto',
  };
  const grid: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns: '1.15fr 0.85fr',
    gap: 'var(--space-9)',
    alignItems: 'stretch',
  };
  const h1: React.CSSProperties = {
    fontWeight: 'var(--weight-extralight)' as any,
    fontSize: 'clamp(2.6rem, 5.2vw, 4.5rem)',
    lineHeight: 1.04,
    letterSpacing: 'var(--tracking-display)',
    color: 'var(--text-strong)',
    margin: 'var(--space-5) 0 0',
    maxWidth: '15ch',
  };
  const subtitle: React.CSSProperties = {
    fontSize: 'var(--text-subtitle)',
    lineHeight: 'var(--leading-normal)',
    color: 'var(--text-muted)',
    margin: 'var(--space-3) 0 0',
  };
  const lead: React.CSSProperties = {
    fontSize: 'var(--text-subtitle)',
    lineHeight: 'var(--leading-normal)',
    color: 'var(--text-muted)',
    margin: 'var(--space-6) 0 0',
    maxWidth: '42ch',
  };
  const bullets: React.CSSProperties = {
    ...lead,
    margin: 'var(--space-4) 0 0',
    paddingLeft: '1.4em',
    listStyle: 'disc',
  };
  const ctas: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--space-5)',
    marginTop: 'var(--space-7)',
  };
  const panel: React.CSSProperties = {
    background: 'var(--navy)',
    borderRadius: 'var(--radius-xl)',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    boxShadow: 'var(--shadow-lg)',
  };
  const portrait: React.CSSProperties = {
    width: '100%',
    aspectRatio: '4 / 3',
    objectFit: 'cover',
    objectPosition: 'center top',
    display: 'block',
  };
  const panelBody: React.CSSProperties = {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    gap: 'var(--space-6)',
    padding: 'var(--space-7)',
  };

  return (
    <section style={section} id="top">
      <div style={grid} className="phld-hero-grid">
        <div>
          <Eyebrow>PHLD.AI · Method</Eyebrow>
          <p style={subtitle}>AI for luxury design, architecture &amp; hospitality</p>
          <h1 style={h1}>
            AI that creates space to{' '}
            <span style={{ color: 'var(--periwinkle-deep)' }}>Create</span>.
          </h1>

          <p style={lead}>For 25 years I worked beside your Team:</p>
          <ul style={bullets}>
            <li>architects and designers,</li>
            <li>project managers,</li>
            <li>installers,</li>
            <li>merchandisers.</li>
          </ul>
          <p style={lead}>
            And their talent astonished me. You have the best team possible for the job: the job of
            creating the spaces that reflect Your Brand.
            <br />
            What pained me all those years was how much of their energy and time drained into paper
            trails, reporting, document administration, over-communication and team orchestration.
          </p>
          <p style={lead}>
            After 2 years of stepping out to IT, I come back to You. Now I build the AI that lifts
            that load, so your design team&apos;s work stays solid, and your best people get more
            space and hours to Create.
          </p>

          <div style={ctas}>
            <Button href="#contact" icon={<ArrowLongRight size={18} />}>
              Let&apos;s talk
            </Button>
            <Button variant="link" href="#the-work">
              See where the hours go
            </Button>
          </div>
        </div>

        <div style={panel} className="phld-hero-panel">
          <img
            src="/assets/brand/founder-portrait.png"
            alt="Olesya Tarasova"
            style={portrait}
          />
          <div style={panelBody}>
            <span style={{ color: 'var(--blush)', fontWeight: 'var(--weight-light)' as any, fontSize: 'var(--text-title)' }}>
              Olesya Tarasova
            </span>
            <span style={{ color: 'var(--text-on-ink-muted)', fontSize: 'var(--text-small)', lineHeight: 'var(--leading-normal)' }}>
              25 years building engineering systems for luxury houses: Cartier, Hermès, Marriott,
              and so many others.
              <br />
              Now, AI orchestration and agentic workflows, built as systems for your house.
            </span>
          </div>
        </div>
      </div>
    </section>
  );
}
